import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import StarBorderOutlinedIcon from "@mui/icons-material/StarBorderOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import RestoreIcon from "@mui/icons-material/Restore";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import DriveFileMoveOutlinedIcon from "@mui/icons-material/DriveFileMoveOutlined";
import type { TrashModel } from "target_wasm";
import { ModelElementType } from "@/components/entities";
import { AssetIcon } from "@/components/common/AssetIcon";
import { Spinner } from "@/components/common/Spinner";
import { StatusChip } from "@/components/common/StatusChip";
import { AppearancePicker } from "@/components/common/AppearancePicker";
import { Tooltip } from "@/components/common/Tooltip";
import { TooltipButton } from "@/components/common/TooltipButton";
import { SpaceDotLabel } from "@/components/main/spaces/SpaceDot";
import {
  spaceColor,
  useOrgContextStore,
} from "@/components/main/stores/orgContextStore";
import { EditableName } from "@/components/canvas/utils/EditableName";
import {
  ContextMenu,
  type ContextMenuItem,
} from "@/components/common/ContextMenu";
import { useContextMenu } from "@/components/hooks/useContextMenu";
import {
  cancelOpening,
  notifyRefusal,
  openModel as openModelSession,
  useOpeningModel,
} from "@/components/canvas/modelSession";
import { OpeningSpinner } from "./OpeningSpinner";
import { useMainStateStore } from "@/components/main/stores/mainStateStore";
import { useModelActions } from "@/components/main/utils/models/useModelActions";
import { useMoveModelToSpace } from "@/components/main/utils/models/useMoveModelToSpace";
import { trashRiskColor } from "@/components/main/utils/models/types";
import { DeleteModelDialog } from "@/components/main/utils/models/deletion/DeleteModelDialog";
import { useModelAppearanceUpdate } from "@/components/main/utils/models/useModelAppearance";
import { canEditModelMeta } from "@/components/main/utils/models/types";
import { MAX_MODEL_NAME_CHARS } from "@/components/main/utils/models/modelName";
import { ownershipLabel } from "./selectors";
import { LiveBadge } from "./LiveBadge";
import { CATALOG, MANAGE_ACCESS_MENU_LABEL } from "@/actions/catalog";
import { prefersReducedMotion } from "@/lib/motion";

interface ModelRowProps {
  model: ModelElementType;
  /** Right-aligned mono metadata (e.g. compact relative time). */
  meta?: string;
  /** Spells `meta` out on hover, e.g. "Opened 2h ago". */
  metaTooltip?: string;
  /** Freshly created — plays the settle-in outline animation. */
  isFresh?: boolean;
  /** Offers "Move to space" — the Overview only (D65). */
  movable?: boolean;
  /** Replaces the muted "Owner"/"Shared" word; the trash rendering wins. */
  sub?: React.ReactNode;
  /** Renders a Trash entry: not openable, a countdown for meta, Restore in
   *  place of the star. */
  trash?: {
    countdownLabel: string;
    countdownTooltip: string;
    onRestore: () => void;
    onDeleteForever: () => void;
    /// Absent for a catalog model, which has no org Trash entry to review.
    trashModel?: TrashModel;
    onAnalysisClosed?: () => void;
    /// Set while the model's space is doomed (D53): it comes back with the space.
    restoreLocked?: string;
    /// Listed under its doomed space, whose Restore brings it back.
    hideRestore?: boolean;
    deleteForeverLocked?: boolean;
    /// The deleted space it sits in, named by its review.
    location?: React.ReactNode;
  };
}

/** Fixed column widths, so meta and star line up across every list. */
export const ModelRow: React.FC<ModelRowProps> = ({
  model,
  meta,
  metaTooltip,
  isFresh = false,
  movable = false,
  sub,
  trash,
}) => {
  const navigate = useNavigate();
  const rowRef = useRef<HTMLDivElement>(null);
  const iconRef = useRef<HTMLDivElement>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [isAnalysisOpen, setIsAnalysisOpen] = useState(false);
  const menu = useContextMenu();
  const toggleStar = useMainStateStore((state) => state.toggleStar);
  const isStarred = model.is_starred ?? false;
  // A model scheduled for deletion can't be entered — it's on its way to Trash.
  const canOpen = !trash && !model.scheduled_deletion_date;
  const modelId = String(model.model_id);
  const isOpening = useOpeningModel((s) => s.modelId === modelId);
  const canEdit = canOpen && canEditModelMeta(model);
  const pinnable = !model.is_catalog;

  const actions = useModelActions(model);

  const handleAppearanceChange = useModelAppearanceUpdate(model);
  const moveToSpace = useMoveModelToSpace(model, rowRef);

  const commitName = actions.commitName;

  // The Overview stays up while the model loads; the canvas takes over once
  // it is ready, so the canvas never shows a loading screen for a row open.
  const openModel = async (): Promise<void> => {
    if (!canOpen || isEditing || isPickerOpen || isOpening) return;
    if (!navigator.onLine) return;
    // The canvas route waits on its code; fetching it now overlaps the load.
    import("@/components/canvas/CanvasApp").catch(() => {});
    const result = await openModelSession(modelId);
    if (result === "ready") navigate(`/canvas/${modelId}`);
    else if (result !== "cancelled") notifyRefusal(result);
  };

  useEffect(() => () => cancelOpening(modelId), [modelId]);
  useEffect(() => {
    if (!isOpening) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") cancelOpening(modelId);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpening, modelId]);

  const scheduledTableCount =
    trash?.trashModel?.tables_scheduled_for_deletion_count ?? 0;

  const contextMenuItems: ContextMenuItem[] = trash
    ? [
        {
          label: trash.restoreLocked ? "Restore (with its space)" : "Restore",
          icon: <RestoreIcon className="icon-md" />,
          onClick: trash.onRestore,
          disabled: !!trash.restoreLocked,
        },
        {
          label: "Review scheduled tables",
          icon: <TableChartOutlinedIcon className="icon-md" />,
          onClick: () => setIsAnalysisOpen(true),
          disabled: scheduledTableCount === 0,
        },
        { label: "", onClick: () => {}, divider: true },
        {
          label: "Delete forever",
          icon: <DeleteOutlineOutlinedIcon className="icon-md" />,
          onClick: trash.onDeleteForever,
          disabled: trash.deleteForeverLocked,
          danger: true,
        },
      ]
    : [
        {
          label: "Open",
          icon: <OpenInFullIcon className="icon-md" />,
          onClick: () => void openModel(),
          disabled: !canOpen,
        },
        {
          // The new tab opens the model itself, behind its loading screen.
          label: "Open in a new tab",
          icon: <OpenInNewIcon className="icon-md" />,
          onClick: () =>
            window.open(`/canvas/${modelId}`, "_blank", "noopener,noreferrer"),
          disabled: !canOpen,
        },
        { label: "", onClick: () => {}, divider: true },
        ...(pinnable
          ? [
              {
                label: isStarred ? "Unpin" : "Pin",
                icon: isStarred ? (
                  <StarRoundedIcon className="icon-md" />
                ) : (
                  <StarBorderOutlinedIcon className="icon-md" />
                ),
                onClick: () => void toggleStar(model.model_id, !isStarred),
              },
            ]
          : []),
        {
          label: "Rename",
          icon: <EditOutlinedIcon className="icon-md" />,
          onClick: () => setIsEditing(true),
          disabled: !canEdit,
        },
        {
          label: "Change appearance",
          icon: <PaletteOutlinedIcon className="icon-md" />,
          onClick: () => setIsPickerOpen(true),
          disabled: !canEdit,
        },
        {
          label: MANAGE_ACCESS_MENU_LABEL,
          icon: <CATALOG.manageAccess.Icon className="icon-md" />,
          onClick: actions.openShare,
          disabled: !actions.permissions.canOpenAccess,
        },
        ...(movable
          ? [
              {
                label: "Move to space",
                icon: <DriveFileMoveOutlinedIcon className="icon-md" />,
                onClick: moveToSpace.start,
                disabled: !actions.permissions.canMoveModel,
              },
            ]
          : []),
        {
          label: CATALOG.copyModelId.label,
          icon: <CATALOG.copyModelId.Icon className="icon-md" />,
          onClick: actions.copyModelId,
        },
        { label: "", onClick: () => {}, divider: true },
        {
          label: "Delete",
          icon: <DeleteOutlineOutlinedIcon className="icon-md" />,
          onClick: actions.requestDelete,
          disabled: !actions.permissions.canDeleteModel,
          danger: true,
        },
      ];

  // The popovers are portaled, which takes the pointer off the row and drops
  // `:hover` — pin the row's hover state while any is open.
  const isRowPinned = menu.isOpen || isPickerOpen || moveToSpace.isOpen;

  return (
    <>
      <div
        ref={rowRef}
        className={`group flex items-center gap-2 item-height px-1 rounded-app hover:bg-bg-hover ${
          isRowPinned || isOpening ? "bg-bg-hover" : ""
        } ${canOpen ? "cursor-pointer" : "cursor-default"} ${
          isFresh ? "overview-fresh" : ""
        }`}
        tabIndex={0}
        data-model-id={model.model_id}
        onClick={canOpen ? () => void openModel() : undefined}
        {...menu.triggerProps}
      >
        <div className="relative shrink-0" ref={iconRef}>
          <span
            role={canEdit ? "button" : undefined}
            aria-label={canEdit ? "Change model appearance" : undefined}
            className={`flex items-center justify-center rounded-app p-0.5 ${
              canEdit ? "hover:bg-bg-hover" : ""
            }`}
            onClick={(e) => {
              if (!canEdit || actions.isDeleting || isOpening) return;
              e.stopPropagation();
              // Open-only: the picker ignores anchor mousedowns, so no
              // close-reopen flash.
              setIsPickerOpen(true);
            }}
          >
            {actions.isDeleting ? (
              <Spinner size="sm" />
            ) : isOpening && !prefersReducedMotion() ? (
              <OpeningSpinner color={model.color} />
            ) : (
              <span data-naming-icon className="flex">
                <AssetIcon
                  size="sm"
                  color={model.color}
                  iconIndex={model.icon}
                />
              </span>
            )}
          </span>
          {!trash && <LiveBadge sessions={model.active_sessions ?? []} />}
          <AppearancePicker
            currentColor={model.color}
            currentIcon={model.icon}
            onColorChange={(color) => void handleAppearanceChange({ color })}
            onIconChange={(icon) => void handleAppearanceChange({ icon })}
            isOpen={isPickerOpen}
            onClose={() => setIsPickerOpen(false)}
            disabled={!canEdit}
            anchorRef={iconRef}
          />
        </div>
        <span
          data-naming-name
          className="text-fg-medium flex items-center min-w-0 shrink"
        >
          <EditableName
            name={model.name || "Unnamed Model"}
            isEditing={isEditing}
            canEdit={canEdit}
            editTrigger="icon"
            editColor={model.color || undefined}
            setIsEditing={setIsEditing}
            update={() => {}}
            maxLength={MAX_MODEL_NAME_CHARS}
            commit={commitName}
            abort={() => setIsEditing(false)}
          />
        </span>
        {model.is_app && <StatusChip tone="accent">App</StatusChip>}
        {!trash && (
          <span className="text-fg-muted-regular-small shrink-0">
            {isOpening ? "Opening…" : (sub ?? ownershipLabel(model.relation))}
          </span>
        )}
        {/* Beside the name, as a deleted space's counts are; tinted by the
            worst risk among the scheduled tables. */}
        {trash && scheduledTableCount > 0 && (
          <Tooltip
            title={`${scheduledTableCount} ${scheduledTableCount === 1 ? "table" : "tables"} scheduled for deletion — review or transfer`}
            placement="top"
          >
            <span
              role="button"
              tabIndex={0}
              aria-label={`Review ${scheduledTableCount} scheduled ${scheduledTableCount === 1 ? "table" : "tables"} in ${model.name || "Unnamed Model"}`}
              onClick={(e) => {
                e.stopPropagation();
                setIsAnalysisOpen(true);
              }}
              onKeyDown={(e) => {
                if (e.key !== "Enter" && e.key !== " ") return;
                e.preventDefault();
                e.stopPropagation();
                setIsAnalysisOpen(true);
              }}
              className="shrink-0 text-fg-muted-regular-small cursor-pointer select-none hover:underline"
              style={{
                color: trashRiskColor(
                  trash.trashModel ? [trash.trashModel] : [],
                ),
              }}
            >
              {scheduledTableCount}{" "}
              {scheduledTableCount === 1 ? "table" : "tables"}
            </span>
          </Tooltip>
        )}
        <span className="flex-1" />
        {trash ? (
          <>
            {/* "Delete forever" is irreversible, so it lives in the menu only. */}
            {trash.hideRestore ? (
              // Keeps the columns of the rows whose Restore shows.
              <span className="w-5 shrink-0" />
            ) : (
              <span
                className={`flex items-center gap-1 shrink-0 transition-opacity ${
                  isRowPinned
                    ? "opacity-100"
                    : "opacity-0 group-hover:opacity-100 focus-within:opacity-100"
                }`}
              >
                <TooltipButton
                  title={trash.restoreLocked ?? "Restore"}
                  variant="contextual"
                  size="xs"
                  aria-label="Restore model"
                  disabled={!!trash.restoreLocked}
                  onClick={(e) => {
                    e.stopPropagation();
                    trash.onRestore();
                  }}
                >
                  <RestoreIcon />
                </TooltipButton>
              </span>
            )}
            {/* Last, so the dates line up at the row's end. */}
            <Tooltip title={trash.countdownTooltip} placement="top">
              <span
                className="min-w-6 text-right font-mono text-fg-muted shrink-0 cursor-default"
                style={{
                  fontSize: "var(--font-size-x-small)",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {trash.countdownLabel}
              </span>
            </Tooltip>
          </>
        ) : (
          <>
            {/* The pin's slot sits in the row's open middle, so a hidden pin
                leaves no gap where the row ends, on its last-opened time. */}
            <span
              className={`flex justify-end shrink-0 w-6 transition-opacity ${
                isStarred || isRowPinned
                  ? "opacity-100"
                  : "opacity-0 group-hover:opacity-100 has-[:focus-visible]:opacity-100"
              }`}
            >
              {pinnable && (
                <TooltipButton
                  data-star
                  title={isStarred ? "Unpin" : "Pin"}
                  variant="contextual"
                  size="xs"
                  aria-label={isStarred ? "Unpin model" : "Pin model"}
                  onClick={(e) => {
                    e.stopPropagation();
                    void toggleStar(model.model_id, !isStarred);
                  }}
                >
                  {isStarred ? <StarRoundedIcon /> : <StarBorderOutlinedIcon />}
                </TooltipButton>
              )}
            </span>
            <ModelSpaceLabel model={model} />
            <Tooltip title={metaTooltip}>
              <span
                className="font-mono text-fg-muted shrink-0 pr-0.5"
                style={{
                  fontSize: "var(--font-size-x-small)",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {meta ?? ""}
              </span>
            </Tooltip>
          </>
        )}
      </div>

      <ContextMenu
        items={contextMenuItems}
        position={menu.position}
        onClose={menu.close}
        ariaLabel={`Actions for ${model.name || "Unnamed Model"}`}
      />

      {/* Mounted only while open: one idle dialog per trash row is overhead. */}
      {trash && isAnalysisOpen && (
        <DeleteModelDialog
          open
          onClose={() => {
            setIsAnalysisOpen(false);
            trash.onAnalysisClosed?.();
          }}
          model={model}
          onDelete={async () => setIsAnalysisOpen(false)}
          isFromTrash
          trashModels={trash.trashModel ? [trash.trashModel] : undefined}
          location={trash.location}
        />
      )}

      {movable && moveToSpace.picker}
      {actions.dialogs}
    </>
  );
};

/// A dot that expands into the space's name on hover; nothing for a space
/// outside the active org's tree, or for the space the Overview is narrowed
/// to, whose header already names it.
const ModelSpaceLabel: React.FC<{ model: ModelElementType }> = ({ model }) => {
  const space = useOrgContextStore((s) => s.spaceById(model.space_id));
  const selectedSpaceId = useOrgContextStore((s) => s.selectedSpaceId);
  if (!space || space.space_id === selectedSpaceId) return null;
  // Yields before the model's name does, so its name stays readable.
  return (
    <span className="flex min-w-0 shrink-8 overflow-hidden">
      <SpaceDotLabel name={space.name} color={spaceColor(space)} />
    </span>
  );
};
